import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Brain, Target, TrendingUp, AlertTriangle, Zap, 
  BookOpen, Clock, Award, ChevronRight, BarChart3,
  Flame, Shield, ArrowUp, ArrowDown, Minus, Calendar,
  Lightbulb, AlertCircle, CheckCircle, Info,
  Play, PieChart, Grid3X3, GraduationCap, FileText,
  ChevronDown, ChevronUp, Lock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Cell
} from 'recharts';

import { API_BASE } from '@/services/api';
import { NetScoreCard, NetScoreDetailPanel } from '../components/strategy/NetScoreCard';
import { ProgressTimelineChart } from '../components/strategy/ProgressTimelineChart';
import {
  MainsWritingPerformance,
  MainsRecurringImprovements,
  MainsRubricRadar,
  MainsEngagement,
} from '../components/strategy/MainsAnalyticsSection';
import { CsatPerformanceIntelligence } from '../components/strategy/CsatPerformanceIntelligence';

// CSAT canonical bucket → list of granular sub-topic key fragments.
// Granular topics (e.g. `boats_streams`, `percentage_basics`) roll up into
// these 8 canonical buckets so the CSAT radar reflects actual practice.
const CSAT_BUCKET_MAP = {
  'Quantitative Aptitude': [
    'quantitative', 'numeracy', 'percentage', 'ratio', 'proportion',
    'profit', 'loss', 'simple_interest', 'compound_interest',
    'time_work', 'speed', 'distance', 'trains', 'boats_streams',
    'mensuration', 'geometry', 'algebra', 'quadratic', 'permutation',
    'combination', 'probability', 'number_system', 'average',
  ],
  'Basic Numeracy': ['basic_numeracy', 'numeracy', 'number_system', 'average', 'ratio'],
  'Reading Comprehension': ['reading_comprehension', 'reading', 'comprehension', 'passage'],
  'Logical Reasoning': [
    'logical_reasoning', 'logical', 'syllogism', 'statement_argument',
    'statement_conclusion', 'statement_assumption', 'cause_effect',
    'course_of_action', 'critical_reasoning',
  ],
  'Analytical Reasoning': [
    'analytical_reasoning', 'analytical', 'seating_arrangement',
    'blood_relation', 'direction', 'puzzle', 'coding_decoding',
    'series', 'order_ranking',
  ],
  'Decision Making': ['decision_making', 'decision'],
  'Data Interpretation': [
    'data_interpretation', 'data_sufficiency', 'bar_chart', 'pie_chart',
    'line_graph', 'table', 'caselet',
  ],
  'Mental Ability': [
    'mental_ability', 'mental', 'verbal_ability', 'non_verbal',
    'figure', 'mirror_image', 'cube', 'paper_folding',
  ],
};

// Aggregate practiced granular CSAT topics into a canonical bucket.
// Returns { mastery, accuracy, attempts }.
function matchCsatBucket(bucketName, practiceData) {
  const fragments = (CSAT_BUCKET_MAP[bucketName] || []).map(f => f.toLowerCase());
  const matched = (practiceData || []).filter(t => {
    if (!t || !t.topic) return false;
    const key = String(t.topic).toLowerCase();
    return fragments.some(f => key === f || key.includes(f) || f.includes(key));
  });
  if (matched.length === 0) {
    return { mastery: 0, accuracy: 0, attempts: 0 };
  }
  const totalAttempts = matched.reduce((s, t) => s + (t.total_attempts || 0), 0);
  if (totalAttempts === 0) {
    return { mastery: 0, accuracy: 0, attempts: 0 };
  }
  // Weighted average by attempts
  const weightedMastery = matched.reduce(
    (s, t) => s + (t.mastery_score || 0) * (t.total_attempts || 0), 0
  );
  const totalCorrect = matched.reduce((s, t) => s + (t.correct || 0), 0);
  return {
    mastery: Math.round(weightedMastery / totalAttempts),
    accuracy: Math.round((totalCorrect / totalAttempts) * 1000) / 10,
    attempts: totalAttempts,
  };
}

// Prefer the unified /csat-performance buckets (same source as the
// "CSAT Performance Intelligence" section) so the radar stays consistent.
function bucketFromPerf(bucketName, csatPerf) {
  if (!csatPerf || !Array.isArray(csatPerf.buckets)) return null;
  const b = csatPerf.buckets.find(x => x.name === bucketName);
  if (!b) return null;
  return {
    mastery: Math.round(b.mastery || 0),
    accuracy: Math.round((b.accuracy || 0) * 10) / 10,
    attempts: b.attempts || 0,
  };
}


const StrategyDashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [mistakeData, setMistakeData] = useState(null);
  const [weeklyPlan, setWeeklyPlan] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [syllabusData, setSyllabusData] = useState(null);
  const [pyqData, setPyqData] = useState(null);
  const [gapData, setGapData] = useState(null);
  const [negativeMarking, setNegativeMarking] = useState(null);
  const [progressTimeline, setProgressTimeline] = useState(null);
  const [mainsAnalytics, setMainsAnalytics] = useState(null);
  const [csatPerf, setCsatPerf] = useState(null);
  const [expandedSubject, setExpandedSubject] = useState(null);
  const [examType, setExamType] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchAllData();
  }, [examType]);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Please log in to view analytics.');
        setLoading(false);
        return;
      }
      const headers = { Authorization: `Bearer ${token}` };
      // API_BASE = '/api' on production, 'https://preview.../api' on dev
      const safeFetch = async (url) => {
        try {
          const controller = new AbortController();
          const timer = setTimeout(() => controller.abort(), 20000);
          const res = await fetch(url, { headers, signal: controller.signal });
          clearTimeout(timer);
          if (res.ok) return await res.json();
          return null;
        } catch (e) {
          return null;
        }
      };

      const [dashboard, mistakes, plan, insights, syllabus, pyq, gap, neg, timeline, mains, csatPerformance] = await Promise.all([
        safeFetch(`${API_BASE}/student-strategy/dashboard?exam_type=${examType}`),
        safeFetch(`${API_BASE}/student-strategy/mistake-analysis?exam_type=${examType}`),
        safeFetch(`${API_BASE}/student-strategy/weekly-plan?exam_type=${examType}&hours_per_day=2`),
        safeFetch(`${API_BASE}/student-strategy/ai-insights?exam_type=${examType}`),
        safeFetch(`${API_BASE}/student-strategy/syllabus-coverage`),
        safeFetch(`${API_BASE}/student-strategy/pyq-analysis`),
        safeFetch(`${API_BASE}/student-strategy/gap-analysis`),
        safeFetch(`${API_BASE}/student-strategy/negative-marking?days=90`),
        safeFetch(`${API_BASE}/student-strategy/progress-timeline?days=60`),
        safeFetch(`${API_BASE}/student-strategy/mains-analytics?days=120`),
        safeFetch(`${API_BASE}/student-strategy/csat-performance?days=365`),
      ]);

      if (dashboard) setData(dashboard);
      if (mistakes) setMistakeData(mistakes);
      if (plan) setWeeklyPlan(plan);
      if (insights) setAiInsights(insights);
      if (syllabus) setSyllabusData(syllabus);
      if (pyq) setPyqData(pyq);
      if (gap) setGapData(gap);
      if (neg) setNegativeMarking(neg);
      if (timeline) setProgressTimeline(timeline);
      if (mains) setMainsAnalytics(mains);
      if (csatPerformance) setCsatPerf(csatPerformance);

      if (!dashboard) {
        setError('Could not load analytics. Please refresh or try again.');
      }
    } catch (err) {
      console.error('Analytics error:', err);
      setError('Something went wrong. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const getMasteryColor = (score) => {
    if (score >= 80) return 'text-emerald-500';
    if (score >= 60) return 'text-blue-500';
    if (score >= 40) return 'text-amber-500';
    return 'text-red-500';
  };

  const getUrgencyStyle = (urgency) => {
    const styles = {
      critical: 'bg-red-100 text-red-700 border-red-200',
      high: 'bg-orange-100 text-orange-700 border-orange-200',
      medium: 'bg-amber-100 text-amber-700 border-amber-200',
      low: 'bg-slate-100 text-slate-600 border-slate-200'
    };
    return styles[urgency] || styles.medium;
  };

  const getTrendIcon = (trend) => {
    if (trend === 'improving') return <ArrowUp className="w-4 h-4 text-emerald-500" />;
    if (trend === 'declining') return <ArrowDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  const getSeverityColor = (severity) => {
    const colors = {
      high: 'bg-red-100 text-red-700 border-red-300',
      medium: 'bg-amber-100 text-amber-700 border-amber-300',
      low: 'bg-slate-100 text-slate-600 border-slate-300'
    };
    return colors[severity] || colors.medium;
  };

  const getActivityColor = (type) => {
    const colors = {
      intensive_practice: 'bg-red-500',
      practice: 'bg-blue-500',
      maintenance: 'bg-emerald-500',
      revision: 'bg-purple-500',
      mock_test: 'bg-indigo-500',
      review: 'bg-amber-500'
    };
    return colors[type] || 'bg-slate-500';
  };

  const getInsightIcon = (type) => {
    const icons = {
      achievement: <Award className="w-5 h-5 text-amber-500" />,
      progress: <TrendingUp className="w-5 h-5 text-blue-500" />,
      focus: <Target className="w-5 h-5 text-red-500" />,
      trend: <TrendingUp className="w-5 h-5 text-emerald-500" />,
      warning: <AlertTriangle className="w-5 h-5 text-orange-500" />,
      action: <Zap className="w-5 h-5 text-purple-500" />,
      strength: <Shield className="w-5 h-5 text-emerald-500" />,
      advice: <Lightbulb className="w-5 h-5 text-amber-500" />,
      milestone: <CheckCircle className="w-5 h-5 text-emerald-500" />
    };
    return icons[type] || <Info className="w-5 h-5 text-slate-500" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Brain className="w-12 h-12 text-indigo-500 animate-pulse mx-auto mb-4" />
          <p className="text-slate-600">Analyzing your performance...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center text-red-500">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Show "Start Your Strategy Journey" only when there are no topics to display
  // This allows unpracticed syllabus topics to be shown even with 0 practice data
  if (!data || (data.total_questions_attempted === 0 && (!data.topic_mastery || data.topic_mastery.length === 0))) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-4xl mx-auto">
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8 text-center">
              <Brain className="w-16 h-16 text-indigo-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Start Your Strategy Journey</h2>
              <p className="text-slate-600 mb-6">
                Practice some questions to unlock your personalized strategy dashboard.
              </p>
              <button
                onClick={() => navigate('/csat-suite')}
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Start Practicing
              </button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50/30">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Brain className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Strategy Analytics</h1>
                <p className="text-sm text-slate-500">Personalized exam preparation guide</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {data.user_tier === 'PRO' && (
                <Badge className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white border-0">
                  PRO
                </Badge>
              )}
              {data.user_tier === 'FREE' && (
                <Badge 
                  variant="outline" 
                  className="cursor-pointer hover:bg-indigo-50 text-indigo-600 border-indigo-300"
                  onClick={() => navigate('/pricing')}
                >
                  Upgrade to PRO
                </Badge>
              )}
              <select
                value={examType}
                onChange={(e) => setExamType(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                data-testid="exam-type-filter"
              >
                <option value="all">All Subjects</option>
                <option value="csat">CSAT Only</option>
                <option value="gs">GS Prelims Only</option>
                <option value="mains">Mains Only</option>
                <option value="prelims">Prelims (GS + CSAT)</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-6 h-12 bg-white shadow-sm">
            <TabsTrigger value="overview" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700" data-testid="tab-overview">
              <Brain className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Overview</span>
            </TabsTrigger>
            <TabsTrigger value="syllabus" className="data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700" data-testid="tab-syllabus">
              <GraduationCap className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Syllabus</span>
            </TabsTrigger>
            <TabsTrigger value="visual" className="data-[state=active]:bg-cyan-50 data-[state=active]:text-cyan-700" data-testid="tab-visual">
              <PieChart className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Visual</span>
            </TabsTrigger>
            <TabsTrigger 
              value="mistakes" 
              className="data-[state=active]:bg-red-50 data-[state=active]:text-red-700" 
              data-testid="tab-mistakes"
              disabled={data.user_tier === 'FREE'}
            >
              <AlertCircle className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Mistakes</span>
              {data.user_tier === 'FREE' && <Lock className="w-3 h-3 ml-1 text-slate-400" />}
            </TabsTrigger>
            <TabsTrigger 
              value="plan" 
              className="data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700" 
              data-testid="tab-plan"
              disabled={data.user_tier === 'FREE'}
            >
              <Calendar className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Plan</span>
              {data.user_tier === 'FREE' && <Lock className="w-3 h-3 ml-1 text-slate-400" />}
            </TabsTrigger>
            <TabsTrigger 
              value="insights" 
              className="data-[state=active]:bg-purple-50 data-[state=active]:text-purple-700" 
              data-testid="tab-insights"
              disabled={data.user_tier === 'FREE'}
            >
              <Lightbulb className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">AI</span>
              {data.user_tier === 'FREE' && <Lock className="w-3 h-3 ml-1 text-slate-400" />}
            </TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="space-y-6" data-testid="overview-content">
            {(() => {
              // Filter data based on examType for Overview tab
              const filterByExamType = (items, categoryField = 'category') => {
                if (!items) return [];
                if (examType === 'all') return items;
                if (examType === 'csat') return items.filter(t => t[categoryField] === 'csat');
                if (examType === 'gs') return items.filter(t => t[categoryField] === 'gs');
                if (examType === 'mains') return items.filter(t => t[categoryField] === 'mains');
                if (examType === 'prelims') return items.filter(t => t[categoryField] === 'csat' || t[categoryField] === 'gs');
                return items;
              };
              
              const filteredPriorities = filterByExamType(data.study_priorities);
              const filteredWeakTopics = filterByExamType(data.weak_topics);
              const filteredTopicMastery = filterByExamType(data.topic_mastery);
              const filteredStrengths = filterByExamType(data.strength_topics);
              
              const filterLabel = examType === 'all' ? 'All' : 
                examType === 'csat' ? 'CSAT' : 
                examType === 'gs' ? 'GS Prelims' : 
                examType === 'mains' ? 'Mains' : 'Prelims';

              return (
                <>
                  {/* Top Insight */}
                  <Card className="border-0 shadow-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className="p-2 bg-white/20 rounded-lg">
                          <Zap className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="font-semibold mb-1">Top Insight</h3>
                          <p className="text-white/90 text-sm">{data.top_insight}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Negative Marking detailed panel — hidden in Mains-only view */}
                  <NetScoreDetailPanel data={negativeMarking} examType={examType} />

                  {/* Mains-specific sections — visible in Mains or All Subjects view */}
                  <MainsWritingPerformance data={mainsAnalytics} examType={examType} />
                  <MainsEngagement data={mainsAnalytics} examType={examType} />
                  <MainsRecurringImprovements data={mainsAnalytics} examType={examType} />

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Study Priorities */}
                    <Card className="border-0 shadow-lg" data-testid="study-priorities-section">
                      <CardHeader className="pb-2">
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <Flame className="w-5 h-5 text-orange-500" />
                          Study Priorities
                          <Badge variant="outline" className="text-xs ml-auto">{filterLabel}</Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        {filteredPriorities.slice(0, 5).map((priority, idx) => (
                          <div key={priority.topic} className="flex items-center gap-3 p-2 rounded-lg bg-slate-50" data-testid={`priority-item-${idx}`}>
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-xs
                              ${idx === 0 ? 'bg-red-500' : idx === 1 ? 'bg-orange-500' : idx === 2 ? 'bg-amber-500' : 'bg-slate-400'}`}>
                              {idx + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-800 text-sm truncate">{priority.topic_name}</p>
                              <p className="text-xs text-slate-500">{priority.reason}</p>
                            </div>
                            <Badge variant="outline" className="text-xs">{priority.priority_score.toFixed(0)}</Badge>
                          </div>
                        ))}
                        {filteredPriorities.length === 0 && (
                          <div className="text-center py-4">
                            <Target className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                            <p className="text-slate-500 text-sm">No {filterLabel} priorities yet</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Weak Topics */}
                    <Card className="border-0 shadow-lg" data-testid="weak-topics-section">
                      <CardHeader className="pb-2">
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <AlertTriangle className="w-5 h-5 text-red-500" />
                          Weak Areas
                          <Badge variant="outline" className="text-xs ml-auto">{filterLabel}</Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        {filteredWeakTopics.slice(0, 4).map((weak, idx) => (
                          <div key={weak.topic} className={`p-2 rounded-lg border ${getUrgencyStyle(weak.urgency || 'high')}`} data-testid={`weak-topic-${idx}`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-sm">{weak.topic_name}</span>
                              <Badge className={`text-xs ${getUrgencyStyle(weak.urgency || 'high')}`}>{(weak.urgency || 'NEW').toUpperCase()}</Badge>
                            </div>
                            <div className="flex items-center justify-between text-xs">
                              <span>Accuracy: {weak.accuracy || 0}%</span>
                              <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{weak.recommended_hours || 8}h</span>
                            </div>
                          </div>
                        ))}
                        {filteredWeakTopics.length === 0 && (
                          <div className="text-center py-4">
                            <Shield className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                            <p className="text-slate-600 text-sm">No {filterLabel} weaknesses!</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Topic Mastery Grid */}
                  <Card className="border-0 shadow-lg" data-testid="topic-mastery-section">
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <TrendingUp className="w-5 h-5 text-indigo-500" />
                        Topic Mastery
                        <Badge variant="outline" className="text-xs ml-auto">{filterLabel}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {filteredTopicMastery.length > 0 ? (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                          {filteredTopicMastery.slice(0, 8).map((topic, idx) => (
                            <div key={topic.topic} className="p-3 rounded-xl bg-slate-50" data-testid={`mastery-topic-${idx}`}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium text-slate-800 text-sm truncate">{topic.topic_name}</span>
                                {getTrendIcon(topic.trend)}
                              </div>
                              <div className={`text-xl font-bold ${getMasteryColor(topic.mastery_score)}`}>
                                {topic.mastery_score}<span className="text-xs text-slate-400">/100</span>
                              </div>
                              <Progress value={topic.mastery_score} className="h-1.5 mt-2" />
                              <div className="flex justify-between text-xs text-slate-500 mt-2">
                                <span>{topic.accuracy}% acc</span>
                                <span>{topic.total_attempts} Qs</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                          <p className="text-slate-500">No {filterLabel} topics practiced yet</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Strengths */}
                  {filteredStrengths.length > 0 && (
                    <Card className="border-0 shadow-lg bg-gradient-to-r from-emerald-50 to-teal-50" data-testid="strength-topics-section">
                      <CardHeader className="pb-2">
                        <CardTitle className="flex items-center gap-2 text-lg text-emerald-700">
                          <Award className="w-5 h-5 text-emerald-500" />
                          Your Strengths
                          <Badge variant="outline" className="text-xs ml-auto border-emerald-300">{filterLabel}</Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-2">
                          {filteredStrengths.map((s, idx) => (
                            <Badge key={s.topic} variant="outline" className="px-3 py-1 bg-white border-emerald-200 text-emerald-700" data-testid={`strength-item-${idx}`}>
                              {s.topic_name} • {s.mastery_score}/100
                            </Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              );
            })()}
          </TabsContent>

          {/* SYLLABUS TAB - Phase C: GS Topic Intelligence */}
          <TabsContent value="syllabus" className="space-y-6" data-testid="syllabus-content">
            {syllabusData ? (
              <>
                {/* Syllabus Stats Overview */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="border-0 shadow-md bg-gradient-to-br from-teal-50 to-emerald-50">
                    <CardContent className="p-4 text-center">
                      <GraduationCap className="w-6 h-6 text-teal-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-teal-700">{syllabusData.syllabus_stats?.total_subjects || 0}</div>
                      <p className="text-xs text-teal-600">Subjects</p>
                    </CardContent>
                  </Card>
                  <Card className="border-0 shadow-md bg-gradient-to-br from-blue-50 to-indigo-50">
                    <CardContent className="p-4 text-center">
                      <BookOpen className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-blue-700">{syllabusData.syllabus_stats?.total_subtopics || 0}</div>
                      <p className="text-xs text-blue-600">Subtopics</p>
                    </CardContent>
                  </Card>
                  <Card className="border-0 shadow-md bg-gradient-to-br from-purple-50 to-pink-50">
                    <CardContent className="p-4 text-center">
                      <Lightbulb className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-purple-700">{syllabusData.syllabus_stats?.total_key_concepts || 0}</div>
                      <p className="text-xs text-purple-600">Key Concepts</p>
                    </CardContent>
                  </Card>
                  <Card className="border-0 shadow-md bg-gradient-to-br from-amber-50 to-orange-50">
                    <CardContent className="p-4 text-center">
                      <FileText className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-amber-700">{syllabusData.syllabus_stats?.pyq_years_covered || 0}</div>
                      <p className="text-xs text-amber-600">Years PYQ Data</p>
                    </CardContent>
                  </Card>
                </div>

                {/* PYQ Trends Section */}
                {pyqData && (
                  <Card className="border-0 shadow-lg" data-testid="pyq-trends-section">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-teal-500" />
                        PYQ Trends Analysis (2019-2024)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Trending Up/Down */}
                        <div className="space-y-4">
                          <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-100">
                            <div className="flex items-center gap-2 mb-2">
                              <ArrowUp className="w-4 h-4 text-emerald-600" />
                              <span className="font-semibold text-emerald-700">Trending Up</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {pyqData.trending_up?.map(subject => (
                                <Badge key={subject} className="bg-emerald-100 text-emerald-700 border-emerald-200">
                                  {subject.charAt(0).toUpperCase() + subject.slice(1)}
                                </Badge>
                              ))}
                              {(!pyqData.trending_up || pyqData.trending_up.length === 0) && (
                                <span className="text-sm text-emerald-600">No significant uptrends</span>
                              )}
                            </div>
                          </div>
                          <div className="p-4 rounded-lg bg-red-50 border border-red-100">
                            <div className="flex items-center gap-2 mb-2">
                              <ArrowDown className="w-4 h-4 text-red-600" />
                              <span className="font-semibold text-red-700">Trending Down</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {pyqData.trending_down?.map(subject => (
                                <Badge key={subject} className="bg-red-100 text-red-700 border-red-200">
                                  {subject.charAt(0).toUpperCase() + subject.slice(1)}
                                </Badge>
                              ))}
                              {(!pyqData.trending_down || pyqData.trending_down.length === 0) && (
                                <span className="text-sm text-red-600">No significant downtrends</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Subject-wise Bar Chart */}
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={Object.entries(pyqData.subject_trends || {}).map(([subject, data]) => ({
                                subject: subject.charAt(0).toUpperCase() + subject.slice(1),
                                questions: data.average_questions || 0
                              })).sort((a, b) => b.questions - a.questions)}
                              layout="vertical"
                              margin={{ left: 80, right: 20 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                              <XAxis type="number" domain={[0, 25]} tick={{ fontSize: 11 }} />
                              <YAxis type="category" dataKey="subject" tick={{ fontSize: 11 }} width={75} />
                              <Tooltip 
                                formatter={(value) => [`${value.toFixed(1)} questions/year`, 'Avg Questions']}
                                contentStyle={{ fontSize: 12 }}
                              />
                              <Bar dataKey="questions" radius={[0, 4, 4, 0]}>
                                {Object.entries(pyqData.subject_trends || {}).map(([subject], index) => (
                                  <Cell key={subject} fill={['#14b8a6', '#3b82f6', '#8b5cf6', '#f59e0b', '#10b981', '#ec4899', '#6366f1'][index % 7]} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Subject Cards */}
                <Card className="border-0 shadow-lg" data-testid="subject-cards-section">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-blue-500" />
                      UPSC Prelims Syllabus
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      {syllabusData.subjects?.map((subject) => (
                        <div 
                          key={subject.key} 
                          className="border rounded-lg overflow-hidden bg-white hover:shadow-md transition-shadow"
                          data-testid={`subject-card-${subject.key}`}
                        >
                          <div 
                            className="p-4 cursor-pointer flex items-center justify-between bg-gradient-to-r from-slate-50 to-white"
                            onClick={() => setExpandedSubject(expandedSubject === subject.key ? null : subject.key)}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                subject.trend === 'increasing' ? 'bg-emerald-100' : 
                                subject.trend === 'decreasing' ? 'bg-red-100' : 'bg-slate-100'
                              }`}>
                                <BookOpen className={`w-5 h-5 ${
                                  subject.trend === 'increasing' ? 'text-emerald-600' : 
                                  subject.trend === 'decreasing' ? 'text-red-600' : 'text-slate-600'
                                }`} />
                              </div>
                              <div>
                                <h4 className="font-semibold text-slate-800">{subject.name}</h4>
                                <div className="flex items-center gap-2 text-xs text-slate-500">
                                  <span>{subject.weightage}% weightage</span>
                                  <span>•</span>
                                  <span>~{subject.avg_questions} Q/year</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className={`text-xs ${
                                subject.trend === 'increasing' ? 'border-emerald-200 text-emerald-600' : 
                                subject.trend === 'decreasing' ? 'border-red-200 text-red-600' : 'border-slate-200'
                              }`}>
                                {subject.trend === 'increasing' ? <ArrowUp className="w-3 h-3 mr-1" /> : 
                                 subject.trend === 'decreasing' ? <ArrowDown className="w-3 h-3 mr-1" /> : 
                                 <Minus className="w-3 h-3 mr-1" />}
                                {subject.trend}
                              </Badge>
                              {expandedSubject === subject.key ? 
                                <ChevronUp className="w-4 h-4 text-slate-400" /> : 
                                <ChevronDown className="w-4 h-4 text-slate-400" />
                              }
                            </div>
                          </div>
                          
                          {expandedSubject === subject.key && (
                            <div className="p-4 border-t bg-slate-50/50">
                              <p className="text-xs text-slate-500 mb-3">{subject.subtopics_count} subtopics in this subject</p>
                              <div className="flex flex-wrap gap-1.5">
                                {syllabusData.high_priority_topics
                                  ?.filter(t => t.subject === subject.name)
                                  .slice(0, 6)
                                  .map((topic, idx) => (
                                    <Badge 
                                      key={idx} 
                                      variant="outline" 
                                      className="text-xs bg-white"
                                    >
                                      {topic.topic}
                                      <span className={`ml-1 ${
                                        topic.importance >= 90 ? 'text-red-500' :
                                        topic.importance >= 80 ? 'text-orange-500' :
                                        'text-slate-400'
                                      }`}>
                                        •{topic.importance}
                                      </span>
                                    </Badge>
                                  ))
                                }
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* High Priority Topics */}
                <Card className="border-0 shadow-lg" data-testid="high-priority-section">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="w-5 h-5 text-red-500" />
                      High Priority Topics (Importance ≥85)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-3 gap-3">
                      {syllabusData.high_priority_topics?.slice(0, 12).map((topic, idx) => (
                        <div 
                          key={idx} 
                          className="p-3 rounded-lg border bg-white hover:border-teal-200 hover:bg-teal-50/30 transition-colors"
                          data-testid={`high-priority-topic-${idx}`}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-sm text-slate-800">{topic.topic}</p>
                              <p className="text-xs text-slate-500">{topic.subject}</p>
                            </div>
                            <Badge className={`text-xs ${
                              topic.importance >= 92 ? 'bg-red-100 text-red-700' :
                              topic.importance >= 88 ? 'bg-orange-100 text-orange-700' :
                              'bg-amber-100 text-amber-700'
                            }`}>
                              {topic.importance}
                            </Badge>
                          </div>
                          <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
                            <span className="capitalize">{topic.difficulty}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Gap Analysis Section */}
                {gapData && (
                  <Card className="border-0 shadow-lg bg-gradient-to-br from-rose-50 to-orange-50" data-testid="gap-analysis-section">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Target className="w-5 h-5 text-rose-500" />
                        Gap Analysis
                        <Badge className="ml-2 bg-rose-100 text-rose-700">
                          {gapData.overall_stats?.high_priority_gaps || 0} gaps
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Coverage Stats */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="p-4 rounded-lg bg-white/70 text-center">
                          <div className="text-2xl font-bold text-rose-600">
                            {gapData.overall_stats?.coverage_percent || 0}%
                          </div>
                          <p className="text-xs text-slate-600">Overall Coverage</p>
                        </div>
                        <div className="p-4 rounded-lg bg-white/70 text-center">
                          <div className="text-2xl font-bold text-orange-600">
                            {gapData.overall_stats?.weighted_coverage || 0}%
                          </div>
                          <p className="text-xs text-slate-600">Weighted Coverage</p>
                        </div>
                        <div className="p-4 rounded-lg bg-white/70 text-center">
                          <div className="text-2xl font-bold text-amber-600">
                            {gapData.overall_stats?.topics_practiced || 0}
                          </div>
                          <p className="text-xs text-slate-600">Topics Practiced</p>
                        </div>
                        <div className="p-4 rounded-lg bg-white/70 text-center">
                          <div className="text-2xl font-bold text-red-600">
                            {gapData.overall_stats?.subjects_needing_attention || 0}
                          </div>
                          <p className="text-xs text-slate-600">Subjects Need Focus</p>
                        </div>
                      </div>

                      {/* Subject Coverage Progress Bars */}
                      <div className="space-y-3">
                        <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                          <BarChart3 className="w-4 h-4" />
                          Subject-wise Coverage
                        </h4>
                        <div className="grid md:grid-cols-2 gap-3">
                          {gapData.subject_coverage?.map((subject) => (
                            <div key={subject.key} className="p-3 rounded-lg bg-white/70" data-testid={`gap-subject-${subject.key}`}>
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-slate-700">{subject.name}</span>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-slate-500">{subject.weightage}%</span>
                                  <Badge variant="outline" className={`text-xs ${
                                    subject.status === 'complete' ? 'border-emerald-200 text-emerald-600 bg-emerald-50' :
                                    subject.status === 'partial' ? 'border-amber-200 text-amber-600 bg-amber-50' :
                                    'border-red-200 text-red-600 bg-red-50'
                                  }`}>
                                    {subject.coverage_percent}%
                                  </Badge>
                                </div>
                              </div>
                              <Progress 
                                value={subject.coverage_percent} 
                                className="h-2"
                              />
                              <p className="text-xs text-slate-500 mt-1">
                                {subject.practiced}/{subject.total_subtopics} subtopics practiced
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Unpracticed High-Priority Topics */}
                      {gapData.unpracticed_high_priority?.length > 0 && (
                        <div className="space-y-3">
                          <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-500" />
                            Unpracticed High-Priority Topics
                          </h4>
                          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {gapData.unpracticed_high_priority?.slice(0, 9).map((topic, idx) => (
                              <div 
                                key={idx} 
                                className={`p-3 rounded-lg border ${
                                  topic.urgency === 'critical' ? 'bg-red-50 border-red-200' :
                                  topic.urgency === 'high' ? 'bg-orange-50 border-orange-200' :
                                  'bg-amber-50 border-amber-200'
                                }`}
                                data-testid={`unpracticed-topic-${idx}`}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <p className="font-medium text-sm text-slate-800">{topic.topic}</p>
                                    <p className="text-xs text-slate-500">{topic.subject}</p>
                                  </div>
                                  <Badge className={`text-xs ${
                                    topic.urgency === 'critical' ? 'bg-red-500' :
                                    topic.urgency === 'high' ? 'bg-orange-500' :
                                    'bg-amber-500'
                                  } text-white`}>
                                    {topic.importance}
                                  </Badge>
                                </div>
                                {topic.key_concepts?.length > 0 && (
                                  <div className="mt-2 pt-2 border-t border-slate-200/50">
                                    <p className="text-xs text-slate-500">
                                      Key: {topic.key_concepts.join(', ')}
                                    </p>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Recommendations */}
                      {gapData.recommendations?.length > 0 && (
                        <div className="space-y-3">
                          <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                            <Lightbulb className="w-4 h-4 text-amber-500" />
                            Action Recommendations
                          </h4>
                          <div className="grid md:grid-cols-2 gap-3">
                            {gapData.recommendations?.slice(0, 4).map((rec, idx) => (
                              <div 
                                key={idx} 
                                className="p-4 rounded-lg bg-white border border-slate-200 hover:shadow-md transition-shadow"
                                data-testid={`recommendation-${idx}`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                    rec.priority === 'high' ? 'bg-red-100' :
                                    rec.priority === 'critical' ? 'bg-rose-100' :
                                    'bg-amber-100'
                                  }`}>
                                    {rec.type === 'subject_gap' ? (
                                      <BookOpen className={`w-4 h-4 ${rec.priority === 'high' ? 'text-red-600' : 'text-amber-600'}`} />
                                    ) : rec.type === 'topic_gap' ? (
                                      <Target className={`w-4 h-4 ${rec.priority === 'critical' ? 'text-rose-600' : 'text-orange-600'}`} />
                                    ) : (
                                      <Award className="w-4 h-4 text-amber-600" />
                                    )}
                                  </div>
                                  <div className="flex-1">
                                    <p className="font-medium text-sm text-slate-800">{rec.title}</p>
                                    <p className="text-xs text-slate-500 mt-0.5">{rec.reason}</p>
                                    <p className="text-xs text-slate-600 mt-1 font-medium">{rec.action}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <GraduationCap className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">Loading syllabus data...</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* VISUAL TAB - Filtered by examType */}
          <TabsContent value="visual" className="space-y-6" data-testid="visual-content">
            
            {/* Progress Over Time — line chart spanning Prelims/Mains/CSAT */}
            <ProgressTimelineChart data={progressTimeline} />

            {/* Mains Rubric Radar — only in Mains or All Subjects view */}
            <MainsRubricRadar data={mainsAnalytics} examType={examType} />

            {/* CSAT Section - Show when examType is 'all', 'csat', or 'prelims' */}
            {(examType === 'all' || examType === 'csat' || examType === 'prelims') && (
              <Card className="border-0 shadow-lg" data-testid="csat-radar-chart">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-lg">
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-blue-500" />
                    CSAT Topic Mastery
                    <Badge className="bg-blue-100 text-blue-700 ml-2">Paper II</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="grid lg:grid-cols-2 gap-6">
                    {/* CSAT Radar */}
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart 
                          data={(() => {
                            const allCsatTopics = [
                              'Reading Comprehension', 'Logical Reasoning', 'Quantitative Aptitude',
                              'Data Interpretation', 'Analytical Reasoning', 'Decision Making',
                              'Basic Numeracy', 'Mental Ability'
                            ];
                            const practiceData = data.topic_mastery?.filter(t => t.category === 'csat') || [];
                            return allCsatTopics.map(topic => {
                              const matched = bucketFromPerf(topic, csatPerf) || matchCsatBucket(topic, practiceData);
                              return {
                                topic: topic.length > 10 ? topic.substring(0, 10) + '...' : topic,
                                fullTopic: topic,
                                mastery: matched.mastery,
                                accuracy: matched.accuracy
                              };
                            });
                          })()}
                        >
                          <PolarGrid stroke="#e2e8f0" />
                          <PolarAngleAxis dataKey="topic" tick={{ fill: '#64748b', fontSize: 10 }} />
                          <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: '#94a3b8', fontSize: 9 }} />
                          <Radar name="Mastery" dataKey="mastery" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.4} />
                          <Radar name="Accuracy" dataKey="accuracy" stroke="#10b981" fill="#10b981" fillOpacity={0.3} />
                          <Tooltip content={({ payload }) => {
                            if (payload && payload.length) {
                              const d = payload[0].payload;
                              return (
                                <div className="bg-slate-800 text-white p-2 rounded-lg text-xs">
                                  <p className="font-semibold">{d.fullTopic}</p>
                                  <p>Mastery: {d.mastery}%</p>
                                  <p>Accuracy: {d.accuracy}%</p>
                                  {d.mastery === 0 && <p className="text-amber-300 mt-1">Not practiced yet</p>}
                                </div>
                              );
                            }
                            return null;
                          }} />
                          <Legend />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                    {/* CSAT Heatmap */}
                    <div className="grid grid-cols-2 gap-2 content-start">
                      {(() => {
                        const allCsatTopics = [
                          'Reading Comprehension', 'Logical Reasoning', 'Quantitative Aptitude',
                          'Data Interpretation', 'Analytical Reasoning', 'Decision Making',
                          'Basic Numeracy', 'Mental Ability'
                        ];
                        const practiceData = data.topic_mastery?.filter(t => t.category === 'csat') || [];
                        return allCsatTopics.map((topic, idx) => {
                          const matched = bucketFromPerf(topic, csatPerf) || matchCsatBucket(topic, practiceData);
                          const score = matched.mastery;
                          let bgColor = score >= 80 ? 'bg-blue-600 text-white' : score >= 60 ? 'bg-blue-400 text-white' : score >= 40 ? 'bg-blue-200 text-blue-900' : score >= 20 ? 'bg-orange-300 text-orange-900' : 'bg-slate-100 text-slate-600';
                          return (
                            <div key={idx} className={`p-3 rounded-lg ${bgColor} transition-transform hover:scale-105`}>
                              <div className="text-xs font-medium truncate">{topic}</div>
                              <div className="text-lg font-bold">{score}</div>
                              <div className="text-xs opacity-80">{matched.accuracy}% acc{matched.attempts > 0 ? ` · ${matched.attempts} Qs` : ''}</div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* CSAT Performance Intelligence — granular sub-topic intelligence */}
            {(examType === 'all' || examType === 'csat' || examType === 'prelims') && (
              <CsatPerformanceIntelligence />
            )}

            {/* GS Prelims Section - Show when examType is 'all', 'gs', or 'prelims' */}
            {(examType === 'all' || examType === 'gs' || examType === 'prelims') && (
              <Card className="border-0 shadow-lg" data-testid="gs-radar-chart">
                <CardHeader className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-t-lg">
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-emerald-500" />
                    GS Prelims Topic Mastery
                    <Badge className="bg-emerald-100 text-emerald-700 ml-2">Paper I</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="grid lg:grid-cols-2 gap-6">
                    {/* GS Radar */}
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart 
                          data={(() => {
                            const allGsSubjects = [
                              { key: 'polity', name: 'Polity' },
                              { key: 'economy', name: 'Economy' },
                              { key: 'history', name: 'History' },
                              { key: 'geography', name: 'Geography' },
                              { key: 'environment', name: 'Environment' },
                              { key: 'science_tech', name: 'Science & Tech' },
                              { key: 'current_affairs', name: 'Current Affairs' }
                            ];
                            const practiceData = data.topic_mastery?.filter(t => t.category === 'gs') || [];
                            return allGsSubjects.map(subject => {
                              const practiced = practiceData.find(t => 
                                t.topic?.toLowerCase().includes(subject.key) ||
                                t.topic_name?.toLowerCase().includes(subject.name.toLowerCase().split(' ')[0])
                              );
                              return {
                                topic: subject.name.length > 10 ? subject.name.substring(0, 10) + '...' : subject.name,
                                fullTopic: subject.name,
                                mastery: practiced?.mastery_score || 0,
                                accuracy: practiced?.accuracy || 0
                              };
                            });
                          })()}
                        >
                          <PolarGrid stroke="#e2e8f0" />
                          <PolarAngleAxis dataKey="topic" tick={{ fill: '#64748b', fontSize: 10 }} />
                          <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: '#94a3b8', fontSize: 9 }} />
                          <Radar name="Mastery" dataKey="mastery" stroke="#10b981" fill="#10b981" fillOpacity={0.4} />
                          <Radar name="Accuracy" dataKey="accuracy" stroke="#6366f1" fill="#6366f1" fillOpacity={0.3} />
                          <Tooltip content={({ payload }) => {
                            if (payload && payload.length) {
                              const d = payload[0].payload;
                              return (
                                <div className="bg-slate-800 text-white p-2 rounded-lg text-xs">
                                  <p className="font-semibold">{d.fullTopic}</p>
                                  <p>Mastery: {d.mastery}%</p>
                                  <p>Accuracy: {d.accuracy}%</p>
                                  {d.mastery === 0 && <p className="text-amber-300 mt-1">Not practiced yet</p>}
                                </div>
                              );
                            }
                            return null;
                          }} />
                          <Legend />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                    {/* GS Heatmap */}
                    <div className="grid grid-cols-2 gap-2 content-start">
                      {(() => {
                        const allGsSubjects = ['Polity', 'Economy', 'History', 'Geography', 'Environment', 'Science & Tech', 'Current Affairs'];
                        const practiceData = data.topic_mastery?.filter(t => t.category === 'gs') || [];
                        return allGsSubjects.map((subject, idx) => {
                          const practiced = practiceData.find(t => 
                            t.topic_name?.toLowerCase().includes(subject.toLowerCase().split(' ')[0])
                          );
                          const score = practiced?.mastery_score || 0;
                          let bgColor = score >= 80 ? 'bg-emerald-600 text-white' : score >= 60 ? 'bg-emerald-400 text-white' : score >= 40 ? 'bg-emerald-200 text-emerald-900' : score >= 20 ? 'bg-orange-300 text-orange-900' : 'bg-slate-100 text-slate-600';
                          return (
                            <div key={idx} className={`p-3 rounded-lg ${bgColor} transition-transform hover:scale-105`}>
                              <div className="text-xs font-medium truncate">{subject}</div>
                              <div className="text-lg font-bold">{score}</div>
                              <div className="text-xs opacity-80">{practiced?.accuracy || 0}% acc</div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Mains Section - Show when examType is 'all' or 'mains' */}
            {(examType === 'all' || examType === 'mains') && (
              <Card className="border-0 shadow-lg" data-testid="mains-section">
                <CardHeader className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-t-lg">
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-purple-500" />
                    GS Mains Topic Mastery
                    <Badge className="bg-purple-100 text-purple-700 ml-2">GS I-IV + Essay</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  {(() => {
                    const mainsTopics = (data.topic_mastery || []).filter(t => t.category === 'mains');
                    const anyMastery = mainsTopics.some(t => (t.mastery_score || 0) > 0 || (t.total_attempts || 0) > 0);
                    const evaluatedCount = mainsAnalytics?.total_answers || 0;
                    if (mainsTopics.length > 0 && anyMastery) {
                      return (
                    <>
                      <div className="h-72 mb-6">
                        <ResponsiveContainer width="100%" height="100%">
                          <RadarChart 
                            data={data.topic_mastery
                              .filter(t => t.category === 'mains')
                              .slice(0, 8)
                              .map(t => ({
                                topic: t.topic_name.length > 10 ? t.topic_name.substring(0, 10) + '...' : t.topic_name,
                                mastery: t.mastery_score,
                                accuracy: t.accuracy
                              }))}
                          >
                            <PolarGrid stroke="#e2e8f0" />
                            <PolarAngleAxis dataKey="topic" tick={{ fill: '#64748b', fontSize: 10 }} />
                            <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: '#94a3b8', fontSize: 9 }} />
                            <Radar name="Mastery" dataKey="mastery" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.4} />
                            <Radar name="Accuracy" dataKey="accuracy" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.3} />
                            <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px', color: '#fff' }} />
                            <Legend />
                          </RadarChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                        {data.topic_mastery.filter(t => t.category === 'mains').map((topic, idx) => {
                          const score = topic.mastery_score;
                          let bgColor = score >= 80 ? 'bg-purple-600 text-white' : score >= 60 ? 'bg-purple-400 text-white' : score >= 40 ? 'bg-purple-200 text-purple-900' : score >= 20 ? 'bg-orange-300 text-orange-900' : 'bg-red-400 text-white';
                          return (
                            <div key={idx} className={`p-2 rounded-lg ${bgColor} transition-transform hover:scale-105`}>
                              <div className="text-xs font-medium truncate">{topic.topic_name}</div>
                              <div className="text-sm font-bold">{score}</div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                      );
                    }
                    // No mains topics yet — show empty state CTA
                    return (
                      <div className="py-10 text-center" data-testid="mains-radar-empty-state">
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-purple-50 mb-4">
                          <FileText className="w-7 h-7 text-purple-400" />
                        </div>
                        <p className="font-semibold text-slate-800 text-base mb-1">
                          No subtopic mastery data yet
                        </p>
                        <p className="text-sm text-slate-500 max-w-md mx-auto leading-relaxed">
                          {evaluatedCount > 0 ? (
                            <>
                              You have <span className="font-medium text-slate-700">{evaluatedCount} evaluated answers</span>{' '}
                              but they haven't been tagged with a syllabus subtopic yet. New evaluated
                              answers will automatically appear on this radar.
                            </>
                          ) : (
                            <>Write Mains answers and get them evaluated to unlock your per-subtopic mastery radar across 10 syllabus areas.</>
                          )}
                        </p>
                        <div className="mt-4 flex flex-wrap justify-center gap-1.5">
                          {['Indian Heritage', 'Modern History', 'Indian Society', 'Geography', 'Social Justice', 'Int. Relations', 'Tech & Env', 'Disaster Mgmt', 'Ethics', 'Essay'].map((t) => (
                            <Badge key={t} variant="outline" className="text-[10px] bg-white border-slate-200 text-slate-500">
                              {t}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            )}

            {/* Heatmap Legend */}
            <Card className="border-0 shadow-md">
              <CardContent className="py-4">
                <div className="flex items-center justify-center gap-4 flex-wrap">
                  <span className="text-sm text-slate-600 font-medium">Mastery Legend:</span>
                  <div className="flex items-center gap-1"><div className="w-4 h-4 rounded bg-emerald-600"></div><span className="text-xs">80+ Excellent</span></div>
                  <div className="flex items-center gap-1"><div className="w-4 h-4 rounded bg-emerald-400"></div><span className="text-xs">60-79 Good</span></div>
                  <div className="flex items-center gap-1"><div className="w-4 h-4 rounded bg-emerald-200"></div><span className="text-xs">40-59 Average</span></div>
                  <div className="flex items-center gap-1"><div className="w-4 h-4 rounded bg-orange-300"></div><span className="text-xs">20-39 Weak</span></div>
                  <div className="flex items-center gap-1"><div className="w-4 h-4 rounded bg-slate-100"></div><span className="text-xs">0-19 Not Started</span></div>
                </div>
              </CardContent>
            </Card>

            {/* Performance Comparison - Filtered by examType
                Hidden for CSAT filter since CsatPerformanceIntelligence
                gives a richer, sub-topic-level breakdown above. */}
            {examType !== 'csat' && (() => {
              // Filter topic mastery based on examType
              const getFilteredTopics = () => {
                if (!data.topic_mastery) return [];
                if (examType === 'all') return data.topic_mastery;
                if (examType === 'csat') return data.topic_mastery.filter(t => t.category === 'csat');
                if (examType === 'gs') return data.topic_mastery.filter(t => t.category === 'gs');
                if (examType === 'mains') return data.topic_mastery.filter(t => t.category === 'mains');
                if (examType === 'prelims') return data.topic_mastery.filter(t => t.category === 'csat' || t.category === 'gs');
                return data.topic_mastery;
              };
              const filteredTopics = getFilteredTopics();
              const strengths = filteredTopics.filter(t => t.mastery_score >= 60);
              const weaknesses = filteredTopics.filter(t => t.mastery_score < 50);
              const moderateTopics = filteredTopics.filter(t => t.mastery_score >= 40 && t.mastery_score < 60);
              const weakTopics = filteredTopics.filter(t => t.mastery_score < 40);
              
              // Calculate filtered overall mastery
              const filteredMastery = filteredTopics.length > 0 
                ? Math.round(filteredTopics.reduce((sum, t) => sum + t.mastery_score, 0) / filteredTopics.length)
                : 0;
              
              const filterLabel = examType === 'all' ? 'All' : 
                examType === 'csat' ? 'CSAT' : 
                examType === 'gs' ? 'GS Prelims' : 
                examType === 'mains' ? 'Mains' : 'Prelims';

              return (
                <>
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Strengths Summary */}
                    <Card className="border-0 shadow-lg bg-gradient-to-br from-emerald-50 to-teal-50">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-emerald-700">
                          <Award className="w-5 h-5 text-emerald-500" />
                          Top Strengths
                          <Badge variant="outline" className="text-xs ml-auto">{filterLabel}</Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {strengths.slice(0, 5).map((topic, idx) => (
                            <div key={topic.topic} className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold text-sm">
                                {idx + 1}
                              </div>
                              <div className="flex-1">
                                <div className="font-medium text-emerald-800">{topic.topic_name}</div>
                                <div className="text-xs text-emerald-600">{topic.accuracy}% accuracy</div>
                              </div>
                              <Badge className="bg-emerald-100 text-emerald-700 border-0">
                                {topic.mastery_score}/100
                              </Badge>
                            </div>
                          ))}
                          {strengths.length === 0 && (
                            <p className="text-emerald-600 text-center py-4">Keep practicing to build strengths!</p>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Weaknesses Summary */}
                    <Card className="border-0 shadow-lg bg-gradient-to-br from-red-50 to-orange-50">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-red-700">
                          <AlertTriangle className="w-5 h-5 text-red-500" />
                          Areas to Improve
                          <Badge variant="outline" className="text-xs ml-auto">{filterLabel}</Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {weaknesses.slice(0, 5).map((topic, idx) => (
                            <div key={topic.topic} className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center font-bold text-sm">
                                {idx + 1}
                              </div>
                              <div className="flex-1">
                                <div className="font-medium text-red-800">{topic.topic_name}</div>
                                <div className="text-xs text-red-600">{topic.accuracy}% accuracy</div>
                              </div>
                              <Badge className="bg-red-100 text-red-700 border-0">
                                {topic.mastery_score}/100
                              </Badge>
                            </div>
                          ))}
                          {weaknesses.length === 0 && (
                            <p className="text-red-600 text-center py-4">Great! No critical weaknesses.</p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Quick Stats Grid */}
                  <Card className="border-0 shadow-lg">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-blue-500" />
                        Performance Summary
                        <Badge variant="outline" className="text-xs ml-auto">{filterLabel}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center p-4 rounded-lg bg-indigo-50">
                          <div className="text-3xl font-bold text-indigo-600">{filteredMastery}</div>
                          <div className="text-sm text-indigo-500">Overall Mastery</div>
                        </div>
                        <div className="text-center p-4 rounded-lg bg-emerald-50">
                          <div className="text-3xl font-bold text-emerald-600">{strengths.length}</div>
                          <div className="text-sm text-emerald-500">Strong Topics</div>
                        </div>
                        <div className="text-center p-4 rounded-lg bg-amber-50">
                          <div className="text-3xl font-bold text-amber-600">{moderateTopics.length}</div>
                          <div className="text-sm text-amber-500">Moderate Topics</div>
                        </div>
                        <div className="text-center p-4 rounded-lg bg-red-50">
                          <div className="text-3xl font-bold text-red-600">{weakTopics.length}</div>
                          <div className="text-sm text-red-500">Weak Topics</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </>
              );
            })()}
          </TabsContent>

          {/* MISTAKES TAB */}
          <TabsContent value="mistakes" className="space-y-6" data-testid="mistakes-content">
            {mistakeData && mistakeData.has_data ? (
              <>
                {/* Mistake Summary */}
                <div className="grid grid-cols-3 gap-4">
                  <Card className="border-0 shadow-md">
                    <CardContent className="p-4 text-center">
                      <div className="text-3xl font-bold text-slate-800">{mistakeData.total_questions}</div>
                      <p className="text-xs text-slate-500">Total Questions</p>
                    </CardContent>
                  </Card>
                  <Card className="border-0 shadow-md">
                    <CardContent className="p-4 text-center">
                      <div className="text-3xl font-bold text-red-500">{mistakeData.total_mistakes}</div>
                      <p className="text-xs text-slate-500">Mistakes</p>
                    </CardContent>
                  </Card>
                  <Card className="border-0 shadow-md">
                    <CardContent className="p-4 text-center">
                      <div className="text-3xl font-bold text-emerald-500">{mistakeData.accuracy}%</div>
                      <p className="text-xs text-slate-500">Accuracy</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Mistake Patterns */}
                <Card className="border-0 shadow-lg" data-testid="mistake-patterns-section">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-red-500" />
                      Mistake Patterns
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {mistakeData.patterns?.map((pattern, idx) => (
                      <div key={idx} className={`p-4 rounded-lg border ${getSeverityColor(pattern.severity)}`} data-testid={`mistake-pattern-${idx}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge className={getSeverityColor(pattern.severity)}>{pattern.severity?.toUpperCase()}</Badge>
                            <span className="font-semibold capitalize">{pattern.pattern_type.replace('_', ' ')}</span>
                          </div>
                          <span className="text-lg font-bold">{pattern.percentage}%</span>
                        </div>
                        <p className="text-sm text-slate-600 mb-2">{pattern.description}</p>
                        <div className="bg-white/50 rounded p-2 text-sm">
                          <strong>Fix: </strong>{pattern.recommendation}
                        </div>
                      </div>
                    ))}
                    {(!mistakeData.patterns || mistakeData.patterns.length === 0) && (
                      <div className="text-center py-8">
                        <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                        <p className="text-slate-600">No specific mistake patterns detected yet.</p>
                        <p className="text-sm text-slate-500">Keep practicing to get detailed analysis.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Difficulty Performance */}
                {mistakeData.difficulty_performance && (
                  <Card className="border-0 shadow-lg" data-testid="difficulty-performance-section">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-blue-500" />
                        Performance by Difficulty
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-4 gap-3">
                        {['easy', 'medium', 'hard', 'advanced'].map(diff => {
                          const perfData = mistakeData.difficulty_performance[diff];
                          if (!perfData || perfData.total === 0) return null;
                          return (
                            <div key={diff} className="p-3 rounded-lg bg-slate-50 text-center">
                              <p className="text-xs text-slate-500 uppercase mb-1">{diff}</p>
                              <div className={`text-xl font-bold ${getMasteryColor(perfData.accuracy)}`}>
                                {perfData.accuracy}%
                              </div>
                              <p className="text-xs text-slate-500">{perfData.total} Qs</p>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Improvement Tips */}
                <Card className="border-0 shadow-lg bg-gradient-to-r from-amber-50 to-orange-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-amber-700">
                      <Lightbulb className="w-5 h-5 text-amber-500" />
                      Improvement Tips
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {mistakeData.improvement_tips?.map((tip, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                          <CheckCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">Practice more questions to see mistake analysis</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* WEEKLY PLAN TAB */}
          <TabsContent value="plan" className="space-y-6" data-testid="plan-content">
            {weeklyPlan && weeklyPlan.has_data ? (
              <>
                {/* Plan Summary */}
                <Card className="border-0 shadow-lg bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-lg mb-1">Your Weekly Plan</h3>
                        <p className="text-white/80 text-sm">
                          {weeklyPlan.total_weekly_hours}h total • {weeklyPlan.estimated_questions} questions
                        </p>
                      </div>
                      <Calendar className="w-10 h-10 text-white/50" />
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {weeklyPlan.focus_topics?.slice(0, 3).map((topic, idx) => (
                        <Badge key={idx} className="bg-white/20 text-white border-0">{topic}</Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Daily Plans */}
                <div className="space-y-4" data-testid="weekly-plan-section">
                  {weeklyPlan.weekly_plan?.map((day, dayIdx) => (
                    <Card key={day.day} className="border-0 shadow-md" data-testid={`day-plan-${dayIdx}`}>
                      <CardHeader className="pb-2">
                        <CardTitle className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm
                              ${day.day === 'Sunday' ? 'bg-indigo-500' : day.day === 'Wednesday' ? 'bg-purple-500' : 'bg-slate-600'}`}>
                              {day.day.charAt(0)}
                            </div>
                            <span>{day.day}</span>
                          </div>
                          <Badge variant="outline">{day.total_hours}h</Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {day.activities?.map((activity, actIdx) => (
                            <div key={actIdx} className="flex items-center gap-3 p-2 rounded bg-slate-50">
                              <div className={`w-2 h-8 rounded ${getActivityColor(activity.type)}`}></div>
                              <div className="flex-1">
                                <p className="font-medium text-sm">{activity.activity}</p>
                                <p className="text-xs text-slate-500">{activity.description?.substring(0, 50)}</p>
                              </div>
                              <Badge variant="outline" className="text-xs">{activity.duration_hours}h</Badge>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Tips */}
                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-amber-500" />
                      Study Tips
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {weeklyPlan.tips?.map((tip, idx) => (
                        <div key={idx} className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 text-sm">
                          <CheckCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-700">{tip}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">Practice some questions to get your personalized study plan</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* AI INSIGHTS TAB */}
          <TabsContent value="insights" className="space-y-6" data-testid="insights-content">
            {aiInsights && aiInsights.has_data ? (
              <>
                {/* Motivation Banner */}
                <Card className="border-0 shadow-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-4">
                      <div className="text-4xl">💪</div>
                      <div>
                        <h3 className="font-semibold text-lg mb-1">Your Motivation</h3>
                        <p className="text-white/90">{aiInsights.motivation}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* AI Insights List */}
                <Card className="border-0 shadow-lg" data-testid="ai-insights-section">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Brain className="w-5 h-5 text-purple-500" />
                      AI-Powered Insights
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {aiInsights.insights?.map((insight, idx) => (
                      <div key={idx} className={`p-4 rounded-lg border ${
                        insight.priority === 'high' ? 'border-red-200 bg-red-50' :
                        insight.priority === 'medium' ? 'border-amber-200 bg-amber-50' :
                        'border-slate-200 bg-slate-50'
                      }`} data-testid={`ai-insight-${idx}`}>
                        <div className="flex items-start gap-3">
                          <div className="text-2xl">{insight.icon}</div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-slate-800">{insight.title}</h4>
                              <Badge variant="outline" className={`text-xs ${
                                insight.priority === 'high' ? 'border-red-300 text-red-600' :
                                insight.priority === 'medium' ? 'border-amber-300 text-amber-600' :
                                'border-slate-300 text-slate-600'
                              }`}>
                                {insight.priority}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-600">{insight.message}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <Card className="border-0 shadow-md">
                    <CardContent className="p-5 text-center">
                      <div className={`text-3xl font-bold ${getMasteryColor(aiInsights.overall_accuracy)}`}>
                        {aiInsights.overall_accuracy}%
                      </div>
                      <p className="text-sm text-slate-500 mt-1">Overall Accuracy</p>
                    </CardContent>
                  </Card>
                  <Card className="border-0 shadow-md">
                    <CardContent className="p-5 text-center">
                      <div className="text-3xl font-bold text-indigo-600">{aiInsights.total_questions}</div>
                      <p className="text-sm text-slate-500 mt-1">Questions Analyzed</p>
                    </CardContent>
                  </Card>
                </div>
              </>
            ) : (
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <Brain className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">Practice more questions to unlock AI insights</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default StrategyDashboardPage;
